import { useEffect, useRef, useImperativeHandle } from 'react';

import classNames from 'classnames';

import { FormInputSize } from '../types';

import style from './input.module.scss';

// React does not render null/undefined/booleans, so such adornments must not
// affect layout (padding, wrapper, or an empty positioned span).
const isAdornmentPresent = (node: React.ReactNode): boolean =>
  node !== null && node !== undefined && typeof node !== 'boolean';

export interface InputProps
  extends Omit<
    React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'size' | 'ref' | 'prefix'
  > {
  ref?: React.Ref<HTMLInputElement>;
  highlighted?: boolean;
  size?: FormInputSize;
  autoFocus?: boolean;
  autoSelect?: boolean;
  /** Adornment rendered inside the input on the left (e.g. a `$` currency symbol). */
  prefix?: React.ReactNode;
  /** Adornment rendered inside the input on the right (e.g. a unit). */
  suffix?: React.ReactNode;
}

export function Input({
  ref,
  highlighted,
  size = 'small',
  autoFocus = false,
  autoSelect = false,
  prefix,
  suffix,
  ...props
}: InputProps) {
  const internalRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => internalRef.current!);

  useEffect(() => {
    if ((autoFocus || autoSelect) && internalRef.current) {
      internalRef.current.focus();
      if (autoSelect) {
        internalRef.current.select();
      }
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPrefix = isAdornmentPresent(prefix);
  const hasSuffix = isAdornmentPresent(suffix);

  const input = (
    <input
      draggable={false}
      ref={internalRef}
      {...props}
      className={classNames(
        {
          [style.input]: true,
          [style.sizeSmall]: size === 'small',
          [style.sizeMedium]: size === 'regular',
          [style.sizeLarge]: size === 'large',
          [style.highlighted]: highlighted,
          [style.hasPrefix]: hasPrefix,
          [style.hasSuffix]: hasSuffix,
        },
        props.className,
      )}
    />
  );

  if (!hasPrefix && !hasSuffix) {
    return input;
  }

  return (
    <span className={style.adornedWrapper}>
      {hasPrefix && <span className={style.prefix}>{prefix}</span>}
      {input}
      {hasSuffix && <span className={style.suffix}>{suffix}</span>}
    </span>
  );
}
